import React from "react";
import { Anchor, CloudSun, ShieldAlert } from "lucide-react";
import { Circle, CircleMarker, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Badge } from "@/components/ui/badge";

const mapPanelCopy = {
  en: { title: "Live Marina Map", subtitle: "Interactive marina positions, weather and hazard context", pins: "pins", noCoordsTitle: "No map coordinates available", noCoordsText: "Try broadening your filters to show marinas with known positions.", yourRadius: "Your 100-mile radius is shown", allowRadius: "Allow location to show 100-mile radius", hazard: "Hazard awareness", yourLocation: "Your location · 100 mile radius" },
  he: { title: "מפת מרינות חיה", subtitle: "מיקומי מרינות אינטראקטיביים, מזג אוויר והקשר סיכונים", pins: "סימונים", noCoordsTitle: "אין קואורדינטות זמינות למפה", noCoordsText: "נסו להרחיב את המסננים כדי להציג מרינות עם מיקומים ידועים.", yourRadius: "רדיוס 100 מייל שלך מוצג", allowRadius: "אפשרו מיקום כדי להציג רדיוס 100 מייל", hazard: "מודעות לסיכונים", yourLocation: "המיקום שלך · רדיוס 100 מייל" },
  es: { title: "Mapa de marinas en vivo", subtitle: "Posiciones interactivas de marinas, clima y contexto de riesgos", pins: "pines", noCoordsTitle: "No hay coordenadas de mapa disponibles", noCoordsText: "Prueba a ampliar los filtros para mostrar marinas con posiciones conocidas.", yourRadius: "Se muestra tu radio de 100 millas", allowRadius: "Permite la ubicación para mostrar un radio de 100 millas", hazard: "Conciencia de riesgos", yourLocation: "Tu ubicación · radio de 100 millas" },
  it: { title: "Mappa marine live", subtitle: "Posizioni interattive delle marine, meteo e contesto dei rischi", pins: "pin", noCoordsTitle: "Coordinate mappa non disponibili", noCoordsText: "Prova ad ampliare i filtri per mostrare marine con posizioni note.", yourRadius: "È mostrato il tuo raggio di 100 miglia", allowRadius: "Consenti la posizione per mostrare un raggio di 100 miglia", hazard: "Consapevolezza dei rischi", yourLocation: "La tua posizione · raggio di 100 miglia" },
  fr: { title: "Carte des marinas en direct", subtitle: "Positions interactives des marinas, météo et contexte des dangers", pins: "repères", noCoordsTitle: "Aucune coordonnée cartographique disponible", noCoordsText: "Essayez d’élargir les filtres pour afficher les marinas avec positions connues.", yourRadius: "Votre rayon de 100 milles est affiché", allowRadius: "Autorisez la position pour afficher un rayon de 100 milles", hazard: "Sensibilisation aux dangers", yourLocation: "Votre position · rayon de 100 milles" },
  de: { title: "Live-Marina-Karte", subtitle: "Interaktive Marina-Positionen, Wetter und Gefahrenkontext", pins: "Pins", noCoordsTitle: "Keine Kartenkoordinaten verfügbar", noCoordsText: "Erweitere die Filter, um Marinas mit bekannten Positionen anzuzeigen.", yourRadius: "Dein 100-Meilen-Radius wird angezeigt", allowRadius: "Standort erlauben, um den 100-Meilen-Radius zu zeigen", hazard: "Gefahrenbewusstsein", yourLocation: "Dein Standort · 100-Meilen-Radius" },
  pt: { title: "Mapa de marinas ao vivo", subtitle: "Posições interativas de marinas, clima e contexto de riscos", pins: "pinos", noCoordsTitle: "Sem coordenadas disponíveis no mapa", noCoordsText: "Tente ampliar os filtros para mostrar marinas com posições conhecidas.", yourRadius: "Seu raio de 100 milhas é exibido", allowRadius: "Permita a localização para mostrar um raio de 100 milhas", hazard: "Consciência de riscos", yourLocation: "Sua localização · raio de 100 milhas" },
  ar: { title: "خريطة المراسي المباشرة", subtitle: "مواقع مراسٍ تفاعلية مع الطقس وسياق المخاطر", pins: "علامات", noCoordsTitle: "لا توجد إحداثيات متاحة على الخريطة", noCoordsText: "جرّب توسيع الفلاتر لإظهار المراسي ذات المواقع المعروفة.", yourRadius: "يظهر نطاقك البالغ 100 ميل", allowRadius: "اسمح بالموقع لإظهار نطاق 100 ميل", hazard: "الوعي بالمخاطر", yourLocation: "موقعك · نطاق 100 ميل" },
  el: { title: "Ζωντανός χάρτης μαρινών", subtitle: "Διαδραστικές θέσεις μαρινών, καιρός και πλαίσιο κινδύνων", pins: "σημεία", noCoordsTitle: "Δεν υπάρχουν διαθέσιμες συντεταγμένες χάρτη", noCoordsText: "Δοκιμάστε να διευρύνετε τα φίλτρα για μαρίνες με γνωστές θέσεις.", yourRadius: "Εμφανίζεται η ακτίνα 100 μιλίων σας", allowRadius: "Επιτρέψτε τοποθεσία για εμφάνιση ακτίνας 100 μιλίων", hazard: "Επίγνωση κινδύνων", yourLocation: "Η τοποθεσία σας · ακτίνα 100 μιλίων" },
  ru: { title: "Живая карта марин", subtitle: "Интерактивные позиции марин, погода и контекст рисков", pins: "меток", noCoordsTitle: "Координаты карты недоступны", noCoordsText: "Попробуйте расширить фильтры, чтобы показать марины с известными позициями.", yourRadius: "Показан ваш радиус 100 миль", allowRadius: "Разрешите геолокацию, чтобы показать радиус 100 миль", hazard: "Контроль опасностей", yourLocation: "Ваше местоположение · радиус 100 миль" },
  tr: { title: "Canlı marina haritası", subtitle: "Etkileşimli marina konumları, hava durumu ve tehlike bağlamı", pins: "işaret", noCoordsTitle: "Harita koordinatı yok", noCoordsText: "Bilinen konumlu marinaları göstermek için filtreleri genişletmeyi deneyin.", yourRadius: "100 millik yarıçapınız gösteriliyor", allowRadius: "100 millik yarıçapı göstermek için konuma izin verin", hazard: "Tehlike farkındalığı", yourLocation: "Konumunuz · 100 mil yarıçap" },
  zh: { title: "实时码头地图", subtitle: "交互式码头位置、天气和风险背景", pins: "标记", noCoordsTitle: "没有可用地图坐标", noCoordsText: "尝试放宽筛选条件，以显示已知位置的码头。", yourRadius: "已显示你的 100 英里半径", allowRadius: "允许定位以显示 100 英里半径", hazard: "风险提示", yourLocation: "你的位置 · 100 英里半径" },
};

function marinaPinIcon(active) {
  const width = active ? 38 : 32;
  const height = active ? 48 : 42;
  const color = active ? "#fbbf24" : "#1CA7A6";
  return L.divIcon({
    className: "marina-anchor-pin-icon",
    html: `<svg width="${width}" height="${height}" viewBox="0 0 32 44" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 10px 14px rgba(2,6,23,.35));overflow:visible;">
      <path d="M16 2C8.3 2 2 8.3 2 16c0 10.8 14 26 14 26s14-15.2 14-26C30 8.3 23.7 2 16 2Z" fill="${color}" stroke="white" stroke-width="3"/>
      <g transform="translate(6 5)" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="10" cy="6" r="2.8"/><line x1="10" y1="22" x2="10" y2="9"/><path d="M4 14H1a9 9 0 0 0 18 0h-3"/>
      </g>
    </svg>`,
    iconSize: [width, height],
    iconAnchor: [width / 2, height - 2],
    popupAnchor: [0, -height + 6],
  });
}

function FitToMarinas({ marinas }) {
  const map = useMap();

  React.useEffect(() => {
    const refresh = () => map.invalidateSize({ pan: false });
    const timers = [100, 350, 800].map((delay) => setTimeout(refresh, delay));

    if (marinas.length) {
      const bounds = marinas.map((marina) => [Number(marina.latitude), Number(marina.longitude)]);
      setTimeout(() => map.fitBounds(bounds, { padding: [42, 42], maxZoom: 9 }), 180);
    }

    return () => timers.forEach(clearTimeout);
  }, [map, marinas]);

  return null;
}

export default function PremiumMarinaMapPanel({ marinas = [], selectedId, onSelect, lang = "en" }) {
  const copy = mapPanelCopy[lang] || mapPanelCopy.en;
  const [userPosition, setUserPosition] = React.useState(null);
  const visible = React.useMemo(
    () => marinas.filter((item) => Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude))),
    [marinas]
  );
  const center = userPosition || (visible.length ? [Number(visible[0].latitude), Number(visible[0].longitude)] : [38.5, 20.5]);

  React.useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      setUserPosition([position.coords.latitude, position.coords.longitude]);
    });
  }, []);

  return (
    <section className="overflow-hidden rounded-[2rem] border border-border bg-card text-foreground shadow-2xl shadow-ocean/10">
      <div className="flex items-center justify-between gap-3 border-b border-border p-4">
        <div>
          <h2 className="font-black">{copy.title}</h2>
          <p className="text-xs text-muted-foreground">{copy.subtitle}</p>
        </div>
        <Badge className="bg-primary text-primary-foreground hover:bg-primary">{visible.length} {copy.pins}</Badge>
      </div>

      <div className="relative h-[28rem] overflow-hidden bg-sky-100 sm:h-[34rem] lg:h-[40rem]">
        {visible.length === 0 ? (
          <div className="grid h-full place-items-center bg-slate-900 p-8 text-center">
            <div>
              <Anchor className="mx-auto h-12 w-12 text-cyan-200" />
              <h3 className="mt-4 text-xl font-black">{copy.noCoordsTitle}</h3>
              <p className="mt-2 text-sm text-cyan-50/60">{copy.noCoordsText}</p>
            </div>
          </div>
        ) : (
          <MapContainer key={`marina-anchor-pin-map-v3-${visible.length}`} center={center} zoom={5} scrollWheelZoom={false} preferCanvas className="!block !h-full !w-full !bg-sky-100" style={{ height: "100%", width: "100%", background: "#dbeafe" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              maxZoom={19}
              detectRetina
              updateWhenIdle
              keepBuffer={4}
            />
            <FitToMarinas marinas={visible} />
            {userPosition && (
              <>
                <Circle
                  center={userPosition}
                  radius={160934}
                  pathOptions={{ color: "#0ea5e9", weight: 2, fillColor: "#38bdf8", fillOpacity: 0.12 }}
                />
                <CircleMarker
                  center={userPosition}
                  radius={9}
                  pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#0ea5e9", fillOpacity: 1 }}
                >
                  <Popup><div className="font-bold text-slate-900">{copy.yourLocation}</div></Popup>
                </CircleMarker>
              </>
            )}
            {visible.map((marina) => {
              const active = selectedId === marina.id;
              return (
                <Marker
                  key={`anchor-pin-${marina.id}`}
                  position={[Number(marina.latitude), Number(marina.longitude)]}
                  icon={marinaPinIcon(active)}
                  zIndexOffset={active ? 1000 : 500}
                  eventHandlers={{ click: () => onSelect?.(marina) }}
                >
                  <Popup>
                    <div className="min-w-44">
                      <p className="font-bold text-slate-900">{marina.name}</p>
                      <p className="text-sm text-slate-600">{marina.city}, {marina.country}</p>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}

        <div className="pointer-events-none absolute left-4 top-4 rounded-3xl border border-white/20 bg-slate-950/80 p-3 text-xs text-white backdrop-blur-xl">
          <CloudSun className="mb-1 h-4 w-4 text-cyan-200" /> {userPosition ? copy.yourRadius : copy.allowRadius}
        </div>
        <div className="pointer-events-none absolute bottom-4 right-4 rounded-3xl border border-red-300/30 bg-red-500/15 p-3 text-xs text-red-50 backdrop-blur-xl">
          <ShieldAlert className="mb-1 h-4 w-4" /> {copy.hazard}
        </div>
      </div>
    </section>
  );
}